import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Upload, 
  FileText, 
  Clock, 
  CheckCircle2, 
  XCircle,
  LogOut,
  Plus,
  Trash2,
  Eye,
  Settings,
  FolderOpen,
  Home,
  Shield,
  AlertCircle,
  ArrowRight,
  Users,
  Scale,
  Sparkles,
  BarChart3,
  Loader2,
  Timer,
  Cpu,
  ImageIcon,
  ScanText,
  Search,
  Library,
  Briefcase,
  Receipt
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component
const Logo = ({ size = "default", clickable = false }) => {
  const navigate = useNavigate();
  const sizeClasses = {
    small: "w-8 h-8",
    default: "w-10 h-10",
    large: "w-14 h-14"
  };
  
  const handleClick = () => {
    if (clickable) {
      navigate('/');
    }
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} relative ${clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      onClick={handleClick}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill="none" stroke="#1E3A8A" strokeWidth="2" opacity="0.3"/>
        <ellipse cx="50" cy="50" rx="35" ry="20" fill="none" stroke="#1E3A8A" strokeWidth="2" transform="rotate(-30 50 50)"/>
        <ellipse cx="50" cy="50" rx="35" ry="20" fill="none" stroke="#F59E0B" strokeWidth="2" transform="rotate(30 50 50)"/>
        <ellipse cx="50" cy="50" rx="35" ry="20" fill="none" stroke="#1E40AF" strokeWidth="2" transform="rotate(90 50 50)"/>
        <circle cx="50" cy="50" r="8" fill="#1E3A8A"/>
        <circle cx="50" cy="50" r="4" fill="#F59E0B"/>
      </svg>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <aside className="w-72 bg-gradient-to-b from-[#0F172A] to-[#1E293B] min-h-screen fixed left-0 top-0 flex flex-col">
      {/* Logo Header */}
      <div className="p-6 border-b border-white/10">
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
          data-testid="nav-home"
        >
          <div className="w-10 h-10 relative rounded-lg overflow-hidden bg-white flex-shrink-0">
            <img src="/aks-logo.png" alt="AKS" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-white leading-tight">AKS TAX AND LEGAL<br/>CONSULTANCY</h1>
          </div>
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">Menu</p>
        <ul className="space-y-1">
          <li>
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-home-btn"
            >
              <Home className="w-5 h-5" />
              Home
            </button>
          </li>
          <li>
            <button 
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white shadow-lg"
              data-testid="nav-documents"
            >
              <FolderOpen className="w-5 h-5" />
              {(user?.role === 'admin' || user?.role === 'super_admin') ? 'All Notices' : 'My Notices'}
            </button>
          </li>
          <li>
            <button 
              onClick={() => navigate('/research')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-research"
            >
              <Search className="w-5 h-5" />
              Legal Research
            </button>
          </li>
          <li>
            <button 
              onClick={() => navigate('/library')}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
              data-testid="nav-library"
            >
              <Library className="w-5 h-5" />
              Legal Library
            </button>
          </li>
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <li>
              <button 
                onClick={() => navigate('/admin')}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
                data-testid="nav-admin"
              >
                <Users className="w-5 h-5" />
                User Management
              </button>
            </li>
          )}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <li>
              <button 
                onClick={() => navigate('/admin/usage')}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
                data-testid="nav-usage"
              >
                <BarChart3 className="w-5 h-5" />
                Usage & Costs
              </button>
            </li>
          )}
        </ul>

        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 mt-6 px-3">Billing</p>
            <ul className="space-y-1">
              <li>
                <button
                  onClick={() => navigate('/clients')}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
                  data-testid="nav-clients"
                >
                  <Briefcase className="w-5 h-5" />
                  Clients
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/invoices')}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 hover:text-white transition-all"
                  data-testid="nav-invoices"
                >
                  <Receipt className="w-5 h-5" />
                  Invoices
                </button>
              </li>
            </ul>
          </>
        )}
        
        {/* Quick Info Card */}
        <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-sm font-medium text-white">Secure Portal</span>
          </div>
          <p className="text-xs text-slate-400">
            Your documents are encrypted and handled with strict confidentiality.
          </p>
        </div>
      </nav>
      
      {/* User Profile */}
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-4 p-3 bg-white/5 rounded-xl">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full ring-2 ring-[#F59E0B]" />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center text-white font-medium ring-2 ring-[#F59E0B]">
              {user?.name?.charAt(0) || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">
              {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Administrator' : 'Client'}
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          className="w-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
};

// Document Card Component
const DocumentCard = ({ doc, onView, onDelete, isAdmin }) => {
  const getStatusConfig = () => {
    switch (doc.status) {
      case 'completed':
        return { 
          icon: CheckCircle2, 
          label: 'Reviewed', 
          bgColor: 'bg-green-500/10', 
          textColor: 'text-green-600',
          borderColor: 'border-green-200'
        };
      case 'processing':
        return { 
          icon: Clock, 
          label: 'Processing OCR...', 
          bgColor: 'bg-amber-500/10', 
          textColor: 'text-amber-600',
          borderColor: 'border-amber-200'
        };
      case 'text_extracted':
        return { 
          icon: Clock, 
          label: 'Pending Review', 
          bgColor: 'bg-blue-500/10', 
          textColor: 'text-blue-600',
          borderColor: 'border-blue-200'
        };
      case 'failed':
        return { 
          icon: XCircle, 
          label: 'Failed', 
          bgColor: 'bg-red-500/10', 
          textColor: 'text-red-600',
          borderColor: 'border-red-200'
        };
      default:
        return { 
          icon: Clock, 
          label: doc.status, 
          bgColor: 'bg-slate-500/10', 
          textColor: 'text-slate-600',
          borderColor: 'border-slate-200'
        };
    }
  };

  const status = getStatusConfig();
  const StatusIcon = status.icon;

  return (
    <Card 
      className={`group hover:shadow-xl transition-all duration-300 border-2 ${status.borderColor} hover:border-[#1E3A8A]/30 overflow-hidden`} 
      data-testid={`doc-card-${doc.doc_id}`}
    >
      {/* Status Bar */}
      <div className={`h-1.5 ${doc.status === 'completed' ? 'bg-gradient-to-r from-green-400 to-green-600' : doc.status === 'text_extracted' ? 'bg-gradient-to-r from-blue-400 to-blue-600' : 'bg-gradient-to-r from-amber-400 to-amber-600'}`}></div>
      
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 truncate max-w-[180px]">{doc.filename}</h3>
              <p className="text-sm text-slate-500">{doc.page_count} pages</p>
            </div>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${status.bgColor} ${status.textColor}`}>
            <StatusIcon className={`w-3.5 h-3.5 ${doc.status === 'processing' ? 'animate-spin' : ''}`} />
            <span className="text-xs font-medium">{status.label}</span>
          </div>
        </div>
        
        {/* Show user info for admin */}
        {isAdmin && doc.user_email && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-4 py-2.5 rounded-lg text-sm mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span><strong>Client:</strong> {doc.user_name || doc.user_email}</span>
          </div>
        )}
        
        {doc.summary && (
          <p className="text-sm text-slate-600 line-clamp-2 mb-4 bg-slate-50 p-3 rounded-lg">
            {doc.summary.substring(0, 150)}...
          </p>
        )}
        
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {new Date(doc.created_at).toLocaleDateString()}
          </span>
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onDelete(doc.doc_id)}
              className="text-slate-400 hover:text-red-600 hover:bg-red-50"
              data-testid={`delete-doc-${doc.doc_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button 
              size="sm"
              onClick={() => onView(doc.doc_id)}
              className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] hover:from-[#172554] hover:to-[#1E40AF] text-white shadow-md"
              data-testid={`view-doc-${doc.doc_id}`}
            >
              <Eye className="w-4 h-4 mr-1.5" />
              View
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Upload Zone Component
const UploadZone = ({ onUpload, isUploading, uploadProgress }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type === 'application/pdf') {
      onUpload(files[0]);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      onUpload(file);
    } else {
      toast.error('Please upload a PDF file');
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-all duration-300 ${
        isDragging 
          ? 'border-[#1E3A8A] bg-[#1E3A8A]/5' 
          : 'border-slate-300 hover:border-[#1E3A8A]/50 hover:bg-slate-50'
      }`}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      data-testid="upload-zone"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-32 h-32 bg-[#1E3A8A] rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#F59E0B] rounded-full translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />
      
      {isUploading ? (
        <div className="space-y-4 relative z-10">
          <div className="w-16 h-16 mx-auto relative">
            <div className="absolute inset-0 bg-[#1E3A8A]/20 rounded-full animate-ping"></div>
            <div className="relative w-16 h-16 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white animate-pulse" />
            </div>
          </div>
          <p className="text-slate-700 font-medium">
            {uploadProgress < 80 ? 'Uploading document...' : uploadProgress < 95 ? 'Processing & extracting text...' : 'Finishing up...'}
          </p>
          <p className="text-sm text-slate-500">
            {uploadProgress < 80
              ? 'Sending your document to the server' 
              : 'Running OCR on your document. This may take a moment for scanned PDFs.'}
          </p>
          {uploadProgress > 0 && (
            <div className="max-w-xs mx-auto">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-xs text-slate-400 mt-2">
                {uploadProgress < 80 
                  ? `${Math.round(uploadProgress)}% uploaded` 
                  : 'Finalizing...'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="relative z-10">
          <div className="w-20 h-20 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-xl">
            <Upload className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            Upload Your Legal Notice
          </h3>
          <p className="text-slate-500 mb-4">
            Drag and drop your PDF here, or click to browse
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              Text PDFs
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              Scanned PDFs (OCR)
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              Up to 100+ pages
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Processing Queue Component
const ProcessingQueue = ({ queue, isAdmin }) => {
  if (!queue || queue.length === 0) return null;

  const getStageInfo = (doc) => {
    const stage = doc.processing_stage || 'queued';
    switch (stage) {
      case 'queued':
        return { icon: Clock, label: 'Queued', color: 'text-slate-500', bgColor: 'bg-slate-100' };
      case 'text_extraction':
        return { icon: ScanText, label: 'Extracting text', color: 'text-blue-600', bgColor: 'bg-blue-50' };
      case 'converting':
        return { icon: ImageIcon, label: 'Converting pages to images', color: 'text-amber-600', bgColor: 'bg-amber-50' };
      case 'ocr':
        return { icon: Cpu, label: 'Running OCR', color: 'text-violet-600', bgColor: 'bg-violet-50' };
      case 'completing':
        return { icon: CheckCircle2, label: 'Finishing up', color: 'text-green-600', bgColor: 'bg-green-50' };
      default:
        return { icon: Loader2, label: 'Processing', color: 'text-slate-500', bgColor: 'bg-slate-100' };
    }
  };

  const getProgressPercent = (doc) => {
    const total = doc.total_pages_to_process || doc.page_count || 1;
    const done = doc.pages_processed || 0;
    const stage = doc.processing_stage || 'queued';
    if (stage === 'queued') return 2;
    if (stage === 'text_extraction') return 10;
    if (stage === 'converting') return 20;
    if (stage === 'completing') return 95;
    if (stage === 'ocr' && total > 0) return 20 + (done / total) * 75;
    return 15;
  };

  const formatEta = (seconds) => {
    if (!seconds || seconds <= 0) return 'Almost done';
    if (seconds < 60) return `~${Math.ceil(seconds)}s remaining`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return `~${mins}m ${secs}s remaining`;
  };

  return (
    <Card className="mb-8 border-0 shadow-lg overflow-hidden" data-testid="processing-queue">
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Processing Queue</h2>
            <p className="text-white/80 text-sm">
              {queue.length} document{queue.length !== 1 ? 's' : ''} being processed
            </p>
          </div>
        </div>
      </div>
      <CardContent className="p-4 bg-white space-y-3">
        {queue.map((doc) => {
          const stageInfo = getStageInfo(doc);
          const StageIcon = stageInfo.icon;
          const progress = getProgressPercent(doc);
          const total = doc.total_pages_to_process || doc.page_count || 0;
          const done = doc.pages_processed || 0;

          return (
            <div
              key={doc.doc_id}
              className="border border-slate-200 rounded-xl p-4 hover:border-violet-200 transition-colors"
              data-testid={`queue-item-${doc.doc_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{doc.filename}</p>
                    <p className="text-xs text-slate-500">{doc.page_count} pages</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${stageInfo.bgColor} ${stageInfo.color} flex-shrink-0`}>
                  <StageIcon className={`w-3.5 h-3.5 ${(doc.processing_stage === 'ocr' || doc.processing_stage === 'text_extraction') ? 'animate-pulse' : ''}`} />
                  <span className="text-xs font-medium whitespace-nowrap">{stageInfo.label}</span>
                </div>
              </div>

              {isAdmin && doc.user_email && (
                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                  <Users className="w-3 h-3" /> {doc.user_name || doc.user_email}
                </p>
              )}

              <div className="space-y-2">
                <Progress value={progress} className="h-2" />
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    {doc.processing_stage === 'ocr' && total > 0 && (
                      <span>{done}/{total} pages</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    <span>{formatEta(doc.estimated_seconds_remaining)}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [processingQueue, setProcessingQueue] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchProcessingQueue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents/processing-queue`);
      setProcessingQueue(response.data.queue || []);
    } catch (error) {
      // Silently fail - queue is supplementary info
      console.error('Error fetching processing queue:', error);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
    fetchProcessingQueue();
  }, [fetchDocuments, fetchProcessingQueue]);

  // Poll for processing queue updates (faster interval for real-time feel)
  useEffect(() => {
    const hasProcessing = documents.some(d => d.status === 'processing') || processingQueue.length > 0;
    if (!hasProcessing) return;
    const interval = setInterval(() => {
      fetchDocuments();
      fetchProcessingQueue();
    }, 3000);
    return () => clearInterval(interval);
  }, [documents, processingQueue, fetchDocuments, fetchProcessingQueue]);

  const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks - smaller = more reliable through proxies
  const MAX_CHUNK_RETRIES = 3;

  const handleUpload = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Use chunked upload for files > 2MB to bypass proxy body size limits
      if (file.size > CHUNK_SIZE) {
        await handleChunkedUpload(file);
      } else {
        await handleDirectUpload(file);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Upload timed out. Please check your connection and try again.');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Invalid file. Please upload a valid PDF.');
      } else if (error.response?.status === 413) {
        toast.error('File too large for single upload.');
      } else if (error.response?.status === 500) {
        toast.error('Server error during processing. Please try uploading again.');
      } else if (!error.response) {
        toast.error(error.message || 'Network error. Please check your connection and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload document. Please try again.');
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDirectUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(`${API}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(Math.min(progress * 0.9, 90));
      }
    });

    setUploadProgress(100);
    toast.success('Document uploaded successfully!');
    fetchDocuments();
    fetchProcessingQueue();
    if (response.data.status === 'processing') {
      toast.info(`Processing ${response.data.page_count} pages. Check the queue for progress.`);
    } else {
      navigate(`/document/${response.data.doc_id}`);
    }
  };

  const handleChunkedUpload = async (file) => {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const filename = file.name;

    toast.info(`Uploading ${(file.size / 1024 / 1024).toFixed(1)}MB in ${totalChunks} parts...`);

    // Upload each chunk with retry logic
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      let chunkUploaded = false;
      for (let attempt = 0; attempt < MAX_CHUNK_RETRIES; attempt++) {
        try {
          const formData = new FormData();
          formData.append('file', chunk, `${filename}.part${i}`);

          await axios.post(`${API}/documents/upload-chunk`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'X-Upload-Id': uploadId,
              'X-Chunk-Index': String(i),
              'X-Total-Chunks': String(totalChunks),
              'X-Filename': filename,
            },
            timeout: 60000,
          });
          chunkUploaded = true;
          break;
        } catch (err) {
          console.warn(`Chunk ${i + 1}/${totalChunks} failed (attempt ${attempt + 1}/${MAX_CHUNK_RETRIES}):`, err.message);
          if (attempt < MAX_CHUNK_RETRIES - 1) {
            // Exponential backoff: 1s, 2s, 4s
            await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
          }
        }
      }

      if (!chunkUploaded) {
        throw new Error(`Failed to upload part ${i + 1} of ${totalChunks} after ${MAX_CHUNK_RETRIES} attempts. Please check your connection and try again.`);
      }

      // Progress: chunks are 0-80%, finalize is 80-100%
      setUploadProgress(Math.round(((i + 1) / totalChunks) * 80));
    }

    // Finalize: reassemble and start processing
    setUploadProgress(85);
    const response = await axios.post(`${API}/documents/finalize-upload`, {
      upload_id: uploadId,
      filename: filename,
    }, { timeout: 300000 });

    setUploadProgress(100);
    toast.success('Document uploaded successfully!');
    fetchDocuments();
    fetchProcessingQueue();
    if (response.data.status === 'processing') {
      toast.info(`Processing ${response.data.page_count} pages. Check the queue for progress.`);
    } else {
      navigate(`/document/${response.data.doc_id}`);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleView = (docId) => {
    navigate(`/document/${docId}`);
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Sidebar user={user} onLogout={logout} />
      
      <main className="ml-72 p-8" data-testid="user-dashboard">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {isAdmin ? 'Client Notices' : `Welcome, ${user?.name?.split(' ')[0]}`}
                </h1>
                <p className="text-slate-600">
                  {isAdmin ? 'Review and respond to client notices' : 'Upload your legal notices for expert review'}
                </p>
              </div>
              
              {/* Stats for Admin */}
              {isAdmin && documents.length > 0 && (
                <div className="flex gap-4">
                  <div className="bg-white rounded-xl px-5 py-3 shadow-sm border border-slate-200">
                    <p className="text-2xl font-bold text-[#1E3A8A]">{documents.length}</p>
                    <p className="text-xs text-slate-500">Total Notices</p>
                  </div>
                  <div className="bg-white rounded-xl px-5 py-3 shadow-sm border border-slate-200">
                    <p className="text-2xl font-bold text-amber-500">{documents.filter(d => d.status !== 'completed').length}</p>
                    <p className="text-xs text-slate-500">Pending Review</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Upload Section - Available for all users including admins */}
          <Card className="mb-8 border-0 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Plus className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {isAdmin ? 'Upload Notice for Review' : 'Submit New Notice'}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {isAdmin 
                      ? 'Upload notices received directly from clients for processing' 
                      : 'Our team will review and get back to you within 24-48 hours'}
                  </p>
                </div>
              </div>
            </div>
            <CardContent className="p-6 bg-white">
              <UploadZone 
                onUpload={handleUpload} 
                isUploading={isUploading}
                uploadProgress={uploadProgress}
              />
            </CardContent>
          </Card>

          {/* Processing Queue */}
          <ProcessingQueue queue={processingQueue} isAdmin={isAdmin} />

          {/* Info Banner for Users */}
          {!isAdmin && documents.length === 0 && !isLoading && (
            <Card className="mb-8 border-0 shadow-md bg-gradient-to-r from-amber-50 to-orange-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Scale className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">How It Works</h3>
                    <p className="text-sm text-slate-600 mb-3">
                      Upload your GST, Customs, FEMA, or PMLA notice. Our expert team will review it and contact you with recommendations and next steps.
                    </p>
                    <div className="flex gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Expert Review</span>
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Case Law Research</span>
                      <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Draft Assistance</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Documents Grid */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#0F172A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                {isAdmin ? 'All Client Notices' : 'Your Notices'}
              </h2>
              {documents.length > 0 && (
                <span className="text-sm text-slate-500">{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            
            {isLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-56 bg-white rounded-2xl shadow-sm skeleton"></div>
                ))}
              </div>
            ) : documents.length === 0 ? (
              <Card className="border-0 shadow-md">
                <CardContent className="p-16 text-center">
                  <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <FileText className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-medium text-slate-700 mb-2">
                    {isAdmin ? 'No notices submitted yet' : 'No notices uploaded yet'}
                  </h3>
                  <p className="text-slate-500 mb-6">
                    {isAdmin ? 'Client notices will appear here' : 'Upload your first legal notice to get started'}
                  </p>
                  {!isAdmin && (
                    <Button 
                      onClick={() => document.querySelector('[data-testid="upload-zone"]')?.click()}
                      className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] hover:from-[#172554] hover:to-[#1E40AF]"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Your First Notice
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {documents.map((doc) => (
                  <DocumentCard 
                    key={doc.doc_id} 
                    doc={doc} 
                    onView={handleView}
                    onDelete={handleDelete}
                    isAdmin={isAdmin}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserDashboard;
